import Swal from "sweetalert2";

let logoutHandler = null;

export const setLogoutHandler = (fn) => {
  logoutHandler = fn;
};

// Helper function to handle API responses
export const handleApiResponse = async (response) => {
  // If response is marked as redirected, don't try to parse it
  if (response && response.redirected === true) {
    return { redirected: true };
  }
  
  // Process normal response
  try {
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error parsing response:', error);
    return response;
  }
};

// Wrapper function for standardized API calls
export const apiCall = async (url, options = {}, showSwal = true) => {
  try {
    const response = await apiFetch(url, options, showSwal);
    
    // Check if the response indicates a redirect happened
    if (response && response.redirected === true) {
      console.log('API call was redirected due to auth issue');
      return { redirected: true };
    }
    
    // For successful responses, parse the JSON
    if (response.ok) {
      return await handleApiResponse(response);
    }
    
    // For error responses that weren't redirects
    const errorData = await handleApiResponse(response);
    throw new Error(errorData.message || 'Request failed');
  } catch (error) {
    console.error('API call error:', error);
    
    // Don't re-throw if it was a redirect error - the user is already being redirected
    if (error.message && error.message.includes('redirected to login')) {
      return { redirected: true };
    }
    
    throw error;
  }
};

// Function to attempt token refresh
const refreshAuthToken = async () => {
  try {
    const tokensString = localStorage.getItem('tokens');
    if (!tokensString) return false;
    
    const tokens = JSON.parse(tokensString);
    if (!tokens?.refresh?.token) return false;
    
    // Call the refresh token endpoint
    const response = await fetch('/v1/auth/refresh-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refreshToken: tokens.refresh.token })
    });
    
    if (!response.ok) return false;
    
    const newTokens = await response.json();
    localStorage.setItem('tokens', JSON.stringify(newTokens));
    
    return newTokens.access.token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

// Handle logout process in one centralized place
const handleLogout = () => {
  if (logoutHandler) {
    logoutHandler();
  } else {
    // Fallback if logoutHandler is not set
    console.log('No logout handler set, using fallback');
    // Clean up all auth-related storage
    localStorage.removeItem('tokens');
    localStorage.removeItem('user');
    localStorage.removeItem('authToken'); // For backward compatibility
    sessionStorage.clear();
    
    // Add a delay before redirecting to ensure storage is cleared
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }
  return { ok: false, redirected: true };
};

export const apiFetch = async (url, options = {}, swal = true) => {
  try {
    // Add authentication token
    if (!options.headers) {
      options.headers = {};
    }
    
    // Check if we need to add Authorization header
    if (!options.headers.Authorization) {
      try {
        const tokensString = localStorage.getItem('tokens');
        if (tokensString) {
          const tokens = JSON.parse(tokensString);
          if (tokens?.access?.token) {
            options.headers.Authorization = `Bearer ${tokens.access.token}`;
          }
        }
      } catch (tokenError) {
        console.error('Error getting token from localStorage:', tokenError);
      }
    }

    // Make the request
    let response = await fetch(url, options);
    
    // Handle 401 unauthorized errors
    if (response.status === 401) {
      // Special handling for login endpoint - don't attempt refresh
      if (typeof url === 'string' && url.includes('/auth/login')) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid credentials');
      } 
      
      console.log('Received 401 status - attempting token refresh');
      
      // Try to refresh the token
      const newToken = await refreshAuthToken();
      if (newToken) {
        // If refresh was successful, retry the original request with new token
        console.log('Token refreshed successfully, retrying original request');
        options.headers.Authorization = `Bearer ${newToken}`;
        
        // Retry the original request with the new token
        response = await fetch(url, options);
        if (response.ok) {
          return response;
        }
      }
      
      // If refresh failed or the retried request failed, handle as session expired
      console.log('Token refresh failed or retried request failed, logging out');
      return handleLogout();
    }

    if (response.status === 403) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Request failed');
    }
    
    // Handle rate limiting (429 Too Many Requests)
    if (response.status === 429) {
      const errorMsg = 'Too many requests. Please try again after a few minutes.';
      if (swal) {
        Swal.fire({
          title: 'Rate Limited',
          text: errorMsg,
          icon: 'warning'
        });
      }
      throw new Error(errorMsg);
    }
    
    // Handle 500 errors - be more specific with auth-related errors
    if (response.status === 500) {
      let errorText = '';
      try {
        // Attempt to get the error text from the response
        errorText = await response.text();
        console.log('Error text:', errorText);
      } catch (err) {
        console.error('Failed to parse error text:', err);
      }
      
      // Check for JWT-specific error messages in the error text
      const isJwtError =
        errorText.includes('jwt malformed') ||
        errorText.includes('jwt expired') ||
        errorText.includes('invalid signature') ||
        errorText.includes('invalid token');
      
      if (isJwtError) {
        console.log('Detected JWT-related 500 error, treating as session expired');
        return handleLogout();
      }
      
      // Try to parse JSON for a more detailed error message
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (parseError) {
        console.error('Error parsing JSON error data:', parseError);
      }
      
      // Throw a detailed error if available, otherwise fallback to the error text or a generic message
      throw new Error(errorData?.message || errorText || 'Server error occurred');
    }
    

    if (response.status === 409) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Request failed');
    }

    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Request failed');
    }
    
    if (!response.ok) {
      // Handle other error responses
      try {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Request failed');
      } catch (jsonError) {
        // If we can't parse JSON, use status text
        throw new Error(`Request failed: ${response.statusText || response.status}`);
      }
    }
    
    return response;
  } catch (error) {
    // Check if it's a token-related error from the server response
    if (error.message && (
        error.message.includes('jwt malformed') || 
        error.message.includes('jwt expired') || 
        error.message.includes('invalid signature') ||
        error.message.includes('invalid token')
      )) {
      console.log('Token error detected:', error.message);
      return handleLogout();
    }
    
    // For other errors, show alert if requested
    if (swal) { 
      Swal.fire({
        title: 'Error',
        text: error.message.trim(),
        icon: 'error'
      });
    }
    throw error;
  }
}; 